import React, { useState } from 'react';
import {
  IUserDisableMutation,
  IUserDisableMutationVariables,
  IUserFieldsFragment,
  IUsersWithBasicInfoQuery,
  IUsersWithBasicInfoQueryVariables,
  UserState,
} from '../../../../../utils/apollo/graphqlSchema';
import ConfirmationModal from '../../../../../components/ConfirmationModal';
import { closeModal } from '../../../../../redux/slices/modal';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectModalData } from '../../../../../redux/selectors/modal';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_USERS_WITH_BASIC_INFO,
  USER_DISABLE,
} from '../../../../../utils/apollo/permission';
import { allDictionary } from '../../../../PermissionControls/hooks/usePermissionToggleMutation';
import { components, OptionProps } from 'react-select';
import Avatar from '../../../../../components/Avatar';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../../utils/types';
import AsyncReactSelectMenu from '../../../../../components/Dropdown/StyledReactSelect/AsyncReactSelectMenu';
import { SafeSearchInput } from '../../../../../components/Dropdown/StyledReactSelect/SelectUtils';

interface IProps {
  refetch: () => void;
}

const userStates = [UserState.Active];

const renderOptionItem = (
  props: OptionProps<IUserFieldsFragment, false, never>,
) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <Avatar picture={data.picture} size={16} />
      <div>{data.name}</div>
    </components.Option>
  );
};

const DisableUserModal = ({ refetch }: IProps) => {
  const dispatch = useAppDispatch();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { userId } = useAppSelector(selectModalData) as { userId: string };

  const handleClose = () => {
    dispatch(closeModal());
  };

  const [fetchUsers] = useLazyQuery<
    IUsersWithBasicInfoQuery,
    IUsersWithBasicInfoQueryVariables
  >(GET_USERS_WITH_BASIC_INFO);

  const loadOptions = async (
    inputValue: string,
  ): Promise<IUserFieldsFragment[]> => {
    const { data } = await fetchUsers({
      variables: {
        pagination: { first: 50, after: 0 },
        term: inputValue,
        includes: [],
        userStates,
      },
    });

    return data?.users.entities?.filter((user) => user.id !== userId) ?? [];
  };

  const handleMutationComplete = ({ userDisable }: IUserDisableMutation) => {
    if (printGraphqlErrors(userDisable.errors)) {
      return;
    }
    refetch();
    handleClose();
  };

  const [disableUser, { loading: mutationLoading }] = useMutation<
    IUserDisableMutation,
    IUserDisableMutationVariables
  >(USER_DISABLE, {
    onCompleted: handleMutationComplete,
  });

  const handleDeleteEntity = async () => {
    assertNotNil(selectedUserId);
    await disableUser({
      variables: {
        input: {
          newOwnerId: selectedUserId,
          userId,
        },
      },
    });
  };

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={handleDeleteEntity}
      header="Disable Confirmation"
      confirmActionLabel="Disable"
      modalType="danger"
      disabled={mutationLoading || !selectedUserId}
      trackContext="users"
      trackConfirmAction="delete - confirm"
      trackCancelAction="delete - cancel"
    >
      <div>{allDictionary.user.disableButton.description}</div>
      <AsyncReactSelectMenu
        isMulti={false}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        onChange={(option) => setSelectedUserId(option?.id || null)}
        placeholder="Select new owner to transfer entities"
        components={{
          Option: renderOptionItem,
          Input: SafeSearchInput,
        }}
        styles={{
          control: (provided) => ({
            ...provided,
            marginTop: '12px',
          }),
          option: (base) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            columnGap: '4px',
          }),
        }}
        promiseSearch={loadOptions}
        defaultOptions={true}
      />
    </ConfirmationModal>
  );
};

export default DisableUserModal;
